import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'
import GrowthEngineNudgeCard, {
  getNudgeCardPropsFromRecommendation,
} from 'src/components/growthEngine/GrowthEngineNudgeCard'
import {
  fetchGrowthEngineRecommendations,
  postGrowthEngineRecommendationInteraction,
} from 'src/utils/growthEngineApi'
import { resolveGrowthEngineCtaPath } from 'src/utils/growthEngineCtaRoutes'
import {
  GROWTH_ENGINE_ORDER_SUCCESS_SURFACE,
  applyOrderSuccessRecommendation,
  pickOrderSuccessProduct,
  shouldBlockGrowthEngineCaseWhileOrdering,
  shouldMarkHomeTipAfterLeavingOrderSuccess,
} from 'src/utils/growthEngineOrderSuccess'
import {
  clearGrowthEngineHomeTipBadge,
  markGrowthEngineHomeTipReady,
} from 'src/utils/growthEngineHomeTipBadge'
import {
  PROGRESS_DISCOVERY_AFTER_ORDER_LABEL,
  getVenueProgressPath,
} from 'src/utils/growthEngineProgressDiscovery'

/** Session-scoped intentIds already recorded as `shown` on this surface. */
const shownOrderSuccessIntentIdsThisSession = new Set()

/**
 * One inline business-case card after checkout success.
 * Express decides the case (and hides when adopted / cooled). Never a modal.
 */
const GrowthEngineOrderSuccessCard = ({
  venueId,
  orderData,
  currentPlan,
  className,
  'data-testid': dataTestId = 'growth-engine-order-success-card',
}) => {
  const history = useHistory()
  const location = useLocation()
  const blockedOnCheckout = shouldBlockGrowthEngineCaseWhileOrdering(
    location?.pathname,
  )
  const [recommendation, setRecommendation] = useState(null)
  const [busy, setBusy] = useState(false)
  const fetchGeneration = useRef(0)
  const waitingCaseRef = useRef(false)
  const openedCaseRef = useRef(false)
  const putAwayRef = useRef(false)

  const suggestedProduct = pickOrderSuccessProduct(orderData)
  const product = suggestedProduct?.title || null

  const clearRecommendation = useCallback(() => {
    setRecommendation(null)
  }, [])

  useEffect(() => {
    if (!venueId || blockedOnCheckout) {
      waitingCaseRef.current = false
      setRecommendation(null)
      return undefined
    }

    const generation = ++fetchGeneration.current
    let cancelled = false

    ;(async () => {
      try {
        const result = await fetchGrowthEngineRecommendations(venueId, {
          platform: 'dashboard',
          surface: GROWTH_ENGINE_ORDER_SUCCESS_SURFACE,
          ...(product ? { product } : {}),
        })
        if (cancelled || generation !== fetchGeneration.current) return
        const next = applyOrderSuccessRecommendation(result, {
          shownIntentIds: shownOrderSuccessIntentIdsThisSession,
        })
        waitingCaseRef.current = Boolean(next?.intentId)
        openedCaseRef.current = false
        putAwayRef.current = false
        setRecommendation(next)
      } catch (err) {
        if (!cancelled && generation === fetchGeneration.current) {
          console.warn('Growth Engine order-success recommendation failed', err)
          waitingCaseRef.current = false
          setRecommendation(null)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [venueId, product, blockedOnCheckout])

  useEffect(() => {
    return () => {
      if (
        shouldMarkHomeTipAfterLeavingOrderSuccess({
          hasWaitingCase: waitingCaseRef.current,
          openedCase: openedCaseRef.current,
          putAway: putAwayRef.current,
        })
      ) {
        markGrowthEngineHomeTipReady()
      }
    }
  }, [])

  useEffect(() => {
    if (!venueId || !recommendation?.intentId) return
    if (shownOrderSuccessIntentIdsThisSession.has(recommendation.intentId)) {
      return
    }
    shownOrderSuccessIntentIdsThisSession.add(recommendation.intentId)
    postGrowthEngineRecommendationInteraction(venueId, {
      intentId: recommendation.intentId,
      interaction: 'shown',
      featureId: recommendation.featureId,
      platform: 'dashboard',
      metadata: {
        variantIndex: recommendation.variantIndex,
        surface: GROWTH_ENGINE_ORDER_SUCCESS_SURFACE,
      },
    }).catch(err => {
      console.warn('Growth Engine order-success shown failed', err)
    })
  }, [venueId, recommendation])

  const postInteraction = useCallback(
    async (interaction, metadata = {}) => {
      if (!venueId || !recommendation?.intentId) return
      setBusy(true)
      try {
        await postGrowthEngineRecommendationInteraction(venueId, {
          intentId: recommendation.intentId,
          interaction,
          featureId: recommendation.featureId,
          platform: 'dashboard',
          metadata: {
            variantIndex: recommendation.variantIndex,
            surface: GROWTH_ENGINE_ORDER_SUCCESS_SURFACE,
            ...metadata,
          },
        })
      } finally {
        setBusy(false)
      }
    },
    [venueId, recommendation],
  )

  const handleSnooze = useCallback(async () => {
    try {
      await postInteraction('snoozed')
    } catch (err) {
      console.warn('Growth Engine order-success snooze failed', err)
    }
    putAwayRef.current = true
    waitingCaseRef.current = false
    clearGrowthEngineHomeTipBadge()
    clearRecommendation()
  }, [postInteraction, clearRecommendation])

  const handleDismiss = useCallback(async () => {
    try {
      await postInteraction('dismissed')
    } catch (err) {
      console.warn('Growth Engine order-success dismiss failed', err)
    }
    putAwayRef.current = true
    waitingCaseRef.current = false
    clearGrowthEngineHomeTipBadge()
    clearRecommendation()
  }, [postInteraction, clearRecommendation])

  const handleCta = useCallback(async () => {
    const action = recommendation?.cta?.action
    try {
      await postInteraction('clicked', { ctaAction: action || null })
    } catch (err) {
      console.warn('Growth Engine order-success click failed', err)
    }
    const path = resolveGrowthEngineCtaPath(venueId, action, {
      product: suggestedProduct?.title,
      productId: suggestedProduct?.productId,
      fromOrderSuccess: true,
    })
    openedCaseRef.current = true
    waitingCaseRef.current = false
    clearGrowthEngineHomeTipBadge()
    clearRecommendation()
    if (path) {
      history.push(path)
    }
  }, [
    recommendation,
    postInteraction,
    venueId,
    suggestedProduct,
    clearRecommendation,
    history,
  ])

  if (blockedOnCheckout) return null

  const cardProps = getNudgeCardPropsFromRecommendation(recommendation, {
    currentPlan,
  })
  const progressPath = getVenueProgressPath(venueId)

  const openProgress = () => {
    if (!progressPath) return
    history.push(progressPath)
  }

  if (!cardProps && !progressPath) return null

  return (
    <Slot className={className} data-testid={dataTestId}>
      {cardProps ? (
        <GrowthEngineNudgeCard
          title={cardProps.title}
          body={cardProps.body}
          featureName={cardProps.featureName}
          featureId={cardProps.featureId}
          progress={cardProps.progress}
          ctaLabel={cardProps.ctaLabel}
          honestEmptyNote={cardProps.honestEmptyNote}
          snoozeLabel={cardProps.snoozeLabel}
          dismissLabel={cardProps.dismissLabel}
          onCta={cardProps.ctaAction ? handleCta : undefined}
          onSnooze={handleSnooze}
          onDismiss={handleDismiss}
          busy={busy}
        />
      ) : null}
      {progressPath ? (
        <ProgressLink
          type='link'
          onClick={openProgress}
          data-testid={`${dataTestId}-progress`}
        >
          {PROGRESS_DISCOVERY_AFTER_ORDER_LABEL}
        </ProgressLink>
      ) : null}
    </Slot>
  )
}

const Slot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 auto 24px;
  max-width: 1100px;
  width: 100%;
`

const ProgressLink = styled(Button)`
  && {
    align-self: flex-start;
    height: auto;
    padding: 0;
  }
`

export default GrowthEngineOrderSuccessCard

/** Test helper — reset session shown-tracking between vitest cases. */
export function __resetGrowthEngineOrderSuccessCardSessionForTests() {
  shownOrderSuccessIntentIdsThisSession.clear()
}
